import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Dialog,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Radio,
    RadioGroup,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import mixpanel from 'mixpanel-browser';
import StoreContext from '../../../react-core-lib/components/app/store-context';
import UserApi from '../../../rest-api/user-api';
import ProvetApi from '../../../rest-api/provet-api';
import { useGenerateLink } from './use-generated-link';
import { useSendEmail } from './use-send-email';
import ValidationUtils from '../../../react-core-lib/utils/validation-utils';
import BlockingSpinner from '../../UIControls/BlockingSpinner';
import ErrorDialog from '../../UIControls/api-button/ErrorDialog';
import {
    formatPatientAutocompleteLabel,
    patientAutocompleteNoOptionsText,
    patientAutocompleteSx,
    renderPatientAutocompleteOption,
} from '../../UIControls/patient-autocomplete-option';
import { CLIENT_NAME_LABEL, PATIENT_NAME_LABEL } from '../../helper/patientLabels';
import { useSnackbar } from '../SnackbarProvider';
import {
    isAuthExpiredDialogVisible,
    isAuthExpiredError,
    notifyAuthExpired,
    subscribeAuthExpired,
} from '../../../utils/auth-error-utils';
import { designTokens } from '../../../theme';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '../../../assets/icons/Email.svg';
import WhatsappIcon from '../../../assets/icons/Whatsapp.svg';
import CopyIcon from '../../../assets/icons/Copy.svg';

const shareMethods = [
    { value: 'email', label: 'Email', icon: EmailIcon },
    { value: 'whatsapp', label: 'WhatsApp', icon: WhatsappIcon },
    { value: 'copy', label: 'Copy link', icon: CopyIcon },
];

const inputFieldSx = {
    '& .MuiOutlinedInput-root': {
        height: { xs: 'auto', sm: 52 },
        minHeight: { xs: '36px', sm: 'auto' },
        borderRadius: { xs: '8px', sm: '14px' },
        backgroundColor: '#FFFFFF',
        '& fieldset': {
            borderColor: '#E5E5E5',
            borderWidth: { xs: '0.6px', sm: '1.5px' },
        },
        '&:hover fieldset': {
            borderColor: '#E5E5E5',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#FF669B',
        },
    },
};

const inputFieldPropsSx = {
    fontFamily: 'Inter, sans-serif',
    fontSize: { xs: '14px', sm: '16px' },
    letterSpacing: { xs: '-0.56px', sm: '-0.64px' },
    color: '#616063',
    borderRadius: { xs: '8px', sm: '14px' },
    height: { xs: 'auto', sm: '52px' },
    minHeight: { xs: '36px', sm: 'auto' },
    alignItems: 'center',
    '& fieldset': { borderColor: '#E5E5E5', borderWidth: { xs: '0.6px', sm: '1.5px' } },
    '&:hover fieldset': { borderColor: '#E5E5E5' },
    '&.Mui-focused fieldset': { borderColor: '#FF669B' },
};

const actionButtonSx = {
    backgroundColor: designTokens.colors.secondaryButton,
    color: '#FFFFFF',
    borderRadius: { xs: '100px', sm: 36 },
    width: { xs: '100%', sm: '160px' },
    height: { xs: 'auto', sm: '44px' },
    padding: { xs: '14px 16px', sm: undefined },
    fontSize: { xs: 14, sm: 16 },
    fontWeight: 500,
    lineHeight: { xs: '14px', sm: '16px' },
    letterSpacing: { xs: '-0.28px', sm: '-0.64px' },
    textTransform: 'none',
    '&:hover': {
        backgroundColor: '#e65588',
    },
    '&.Mui-disabled': {
        backgroundColor: 'rgba(255, 102, 155, 0.5)',
        color: '#FFFFFF',
    },
};

const continueButtonSx = {
    backgroundColor: designTokens.colors.secondaryButton,
    color: '#FFFFFF',
    borderRadius: { xs: '100px', sm: 36 },
    width: { xs: '100%', sm: '160px' },
    height: { xs: '35px', sm: '44px' },
    fontSize: { xs: 14, sm: 16 },
    fontWeight: 500,
    lineHeight: { xs: '14px', sm: '16px' },
    letterSpacing: { xs: '-0.28px', sm: '-0.64px' },
    textTransform: 'none',
    '&:hover': {
        backgroundColor: '#e65588',
    },
    '&.Mui-disabled': {
        backgroundColor: 'rgba(255, 102, 155, 0.5)',
        color: '#FFFFFF',
    },
};

const shareTabButtonSx = (selected) => ({
    borderRadius: '272px',
    padding: { xs: '14.5px', sm: '12px 20px' },
    minWidth: 'unset',
    whiteSpace: 'nowrap',
    backgroundColor: selected ? designTokens.colors.primaryButton : designTokens.colors.secondaryButton,
    color: '#FFFFFF',
    textTransform: 'none',
    fontSize: { xs: '14px', sm: '16px' },
    lineHeight: { xs: '14px', sm: '16px' },
    letterSpacing: { xs: '-0.56px', sm: '-0.32px' },
    fontWeight: 500,
    gap: { xs: 0, sm: '8px' },
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
        backgroundColor: selected ? '#0b0f6a' : '#e65588',
    },
});

const iconStyle = {
    width: 22,
    height: 22,
    display: 'block',
    filter: 'brightness(0) invert(1)',
};

const whatsappButtonSx = {
    backgroundColor: '#25D366',
    color: '#FFFFFF',
    borderRadius: { xs: '100px', sm: '30px' },
    width: { xs: '100%', sm: '240px' },
    height: { xs: 'auto', sm: '44px' },
    padding: { xs: '14px 16px', sm: undefined },
    fontSize: { xs: 14, sm: 16 },
    fontWeight: 500,
    lineHeight: { xs: '14px', sm: '16px' },
    letterSpacing: { xs: '-0.28px', sm: '-0.64px' },
    textTransform: 'none',
    '&:hover': {
        backgroundColor: '#1fb75a',
    },
};

const dialogHeights = {
    patient: { manual: 574, select: 465, provet: 507 },
    share: { email: 419, whatsapp: 357, copy: 419 },
};

const radioLabelSx = {
    '& .MuiFormControlLabel-label': {
        fontFamily: 'Inter, sans-serif',
        fontSize: { xs: 14, sm: 18 },
        lineHeight: { xs: '14px', sm: '18px' },
        letterSpacing: { xs: '-0.56px', sm: '-0.72px' },
        color: designTokens.colors.textSecondary,
    },
};

const getBundledHandoutNames = (handouts) =>
    Array.isArray(handouts) ? handouts.map((handout) => handout?.contentName).filter(Boolean) : [];

const getBundledEmailContentName = (handoutNames) =>
    handoutNames.length ? JSON.stringify(handoutNames) : 'your handouts';

const QuickShareDialog = ({ open, onClose, handout }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isShortViewport = useMediaQuery('(max-height:700px)');
    const useCompactDialog = isMobile || isShortViewport;
    const { authStore, userDataStore } = useContext(StoreContext);
    const showSnackbar = useSnackbar();
    const sendEmail = useSendEmail();
    const isBundledHandouts = Array.isArray(handout);
    const bundledHandoutNames = getBundledHandoutNames(handout);
    const shareTargetLabel = isBundledHandouts
        ? bundledHandoutNames.join(', ') || 'your handouts'
        : handout?.contentName || 'your handout';
    const emailContentName = isBundledHandouts ? getBundledEmailContentName(bundledHandoutNames) : shareTargetLabel;
    const { generateLink, generateLinkOnly, createShareData } = useGenerateLink(handout, isBundledHandouts);

    const [step, setStep] = useState('patient');
    const [inputMethod, setInputMethod] = useState('manual');
    const [manualPatientName, setManualPatientName] = useState('');
    const [manualClientSurname, setManualClientSurname] = useState('');
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [selectedProvetPatient, setSelectedProvetPatient] = useState(null);
    const [patients, setPatients] = useState([]);
    const [isLoadingPatients, setIsLoadingPatients] = useState(false);
    const [provetPatients, setProvetPatients] = useState([]);
    const [provetClient, setProvetClient] = useState(null);
    const [provetEmails, setProvetEmails] = useState([]);
    const [shareMethod, setShareMethod] = useState('email');
    const [email, setEmail] = useState('');
    const [emailMessage, setEmailMessage] = useState('');
    const [generatedLink, setGeneratedLink] = useState('');
    const [integrationSource, setIntegrationSource] = useState(null);
    const [selectedProvetPatientUrl, setSelectedProvetPatientUrl] = useState('');
    const [isBlocking, setIsBlocking] = useState(false);
    const [errorDialogOpen, setErrorDialogOpen] = useState(false);
    const [errorDialogTitle, setErrorDialogTitle] = useState('Share error');
    const [errorDialogMessage, setErrorDialogMessage] = useState('There was an error sharing. Please try again.');

    const showProvetOption = useMemo(
        () => Boolean(localStorage.getItem('provet_client')) || provetPatients.length > 0,
        [provetPatients],
    );
    const isSingleProvetPatient = provetPatients.length === 1;

    const resetState = () => {
        setStep('patient');
        setInputMethod('manual');
        setManualPatientName('');
        setManualClientSurname('');
        setSelectedPatient(null);
        setSelectedProvetPatient(null);
        setPatients([]);
        setProvetPatients([]);
        setProvetClient(null);
        setProvetEmails([]);
        setShareMethod('email');
        setEmail('');
        setEmailMessage('');
        setGeneratedLink('');
        setIntegrationSource(null);
        setSelectedProvetPatientUrl('');
        setIsBlocking(false);
        setErrorDialogOpen(false);
    };

    const closeDialog = () => {
        resetState();
        onClose();
    };

    const openAuthAwareErrorDialog = (error) => {
        if (isAuthExpiredError(error)) {
            notifyAuthExpired({ source: 'quick-share' });
            return;
        }
        setErrorDialogTitle('Share error');
        setErrorDialogMessage('There was an error sharing. Please try again.');
        setErrorDialogOpen(true);
    };

    useEffect(() => {
        if (!open || !handout) {
            return;
        }

        let isSubscribed = true;

        const fetchPatients = async () => {
            setIsLoadingPatients(true);
            try {
                const response = await UserApi.getPatientsByFilter(authStore.idToken, {
                    filterType: 'patientStatus',
                    filterValue: 'active',
                });

                if (!isSubscribed) {
                    return;
                }

                if (response?.patients) {
                    setPatients(response.patients);
                    if (response.patients.length === 0) {
                        setInputMethod('manual');
                    }
                } else {
                    setPatients([]);
                    setInputMethod('manual');
                }
            } catch (error) {
                console.error('Failed to fetch patients:', error);
                if (isSubscribed) {
                    setPatients([]);
                    setInputMethod('manual');
                }
            } finally {
                if (isSubscribed) {
                    setIsLoadingPatients(false);
                }
            }
        };

        const fetchProvet = async () => {
            try {
                const client = localStorage.getItem('provet_client');
                if (!client) {
                    setProvetPatients([]);
                    setProvetEmails([]);
                    setProvetClient(null);
                    return;
                }

                const {
                    emails,
                    patients: provetList,
                    client: provetClientData,
                } = await ProvetApi.getClientEmails(authStore.idToken, { client });
                if (!isSubscribed) {
                    return;
                }

                setProvetPatients(provetList || []);
                setProvetEmails(emails || []);
                setProvetClient(provetClientData || null);
            } catch (error) {
                console.warn('Provet prefill failed:', error?.message || error);
                if (isSubscribed) {
                    setProvetPatients([]);
                    setProvetEmails([]);
                    setProvetClient(null);
                }
            }
        };

        fetchPatients();
        fetchProvet();

        return () => {
            isSubscribed = false;
        };
    }, [open, handout, authStore.idToken]);

    useEffect(() => {
        if (!open) {
            resetState();
        }
    }, [open]);

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        if (isAuthExpiredDialogVisible()) {
            closeDialog();
            return undefined;
        }

        return subscribeAuthExpired(() => {
            closeDialog();
        });
    }, [open]);

    useEffect(() => {
        if (provetPatients.length === 1) {
            setSelectedProvetPatient(provetPatients[0]);
        }
    }, [provetPatients]);

    useEffect(() => {
        if (!email && provetEmails.length === 1) {
            setEmail(provetEmails[0]);
        }
    }, [provetEmails, email]);

    const isContinueDisabled = () => {
        if (inputMethod === 'manual') {
            return !manualPatientName && !manualClientSurname;
        }
        if (inputMethod === 'select') {
            return !selectedPatient;
        }
        if (inputMethod === 'provet') {
            return !selectedProvetPatient;
        }
        return true;
    };

    const buildPatientDetails = () => {
        if (inputMethod === 'manual') {
            return {
                patientName: manualPatientName || null,
                clientSurname: manualClientSurname || null,
            };
        }
        if (inputMethod === 'select' && selectedPatient) {
            return {
                patientName: selectedPatient.patientName || null,
                clientSurname: selectedPatient.clientSurname || null,
                patientId: selectedPatient.patientId || null,
                microchipNumber: selectedPatient.microchipNumber || null,
                clinicPatientId: selectedPatient.clinicPatientId || null,
            };
        }
        if (inputMethod === 'provet' && selectedProvetPatient) {
            const provetClientLastName =
                provetClient?.lastName || provetClient?.lastname || provetClient?.surname || null;
            return {
                patientName: selectedProvetPatient.name || null,
                clientSurname: provetClientLastName,
            };
        }
        return null;
    };

    const handleContinue = () => {
        if (inputMethod === 'provet') {
            setIntegrationSource('provet');
            setSelectedProvetPatientUrl(selectedProvetPatient?.url || '');
        } else {
            setIntegrationSource(null);
            setSelectedProvetPatientUrl('');
        }
        setStep('share');
        setGeneratedLink('');
        setShareMethod('email');
    };

    const getShareContext = () => {
        if (integrationSource) {
            return { sharedViaIntegration: true, integrationSource };
        }
        return null;
    };

    const handleSendEmail = async () => {
        if (!ValidationUtils.validateEmail(email)) {
            showSnackbar?.('Please enter a valid email address.', 'error');
            return;
        }

        if (isAuthExpiredDialogVisible()) {
            return;
        }

        setIsBlocking(true);
        try {
            const patientDetails = buildPatientDetails();
            const shareContext = getShareContext();
            const linkId = await generateLink(patientDetails, shareContext);
            const clinicId = userDataStore.userId;

            await sendEmail(email, emailContentName, linkId, clinicId, {}, emailMessage);

            mixpanel.track('HandoutShared', {
                email: userDataStore?.userData?.emailAddress,
                shareMethod: 'Send Email',
                handoutTitle: shareTargetLabel,
            });

            const client = localStorage.getItem('provet_client');
            if (client) {
                try {
                    const userId = localStorage.getItem('provet_user');
                    const timestamp = localStorage.getItem('provet_timestamp');
                    const verification = localStorage.getItem('provet_verify');
                    const patientId = selectedProvetPatientUrl
                        ? selectedProvetPatientUrl.split('/').filter(Boolean).pop()
                        : null;
                    const sentAt = new Date().toLocaleString('en-GB', { timeZone: 'Europe/London', hour12: true });

                    await ProvetApi.addNote(authStore.idToken, {
                        clientId: client,
                        patientId,
                        handoutTitle: shareTargetLabel,
                        linkId,
                        clinicId,
                        sentAt,
                        userId,
                        timestamp,
                        verification,
                    });
                } catch (error) {
                    console.warn('[Provet] Failed to add note:', error?.message || error);
                }
            }

            showSnackbar?.('Email sent successfully.', 'success');
            closeDialog();
        } catch (error) {
            console.error('Quick share email error:', error);
            openAuthAwareErrorDialog(error);
        } finally {
            setIsBlocking(false);
        }
    };

    const handleShareWhatsapp = async () => {
        if (isAuthExpiredDialogVisible()) {
            return;
        }

        setIsBlocking(true);
        try {
            const patientDetails = buildPatientDetails();
            const shareContext = getShareContext();
            const { title, url, message } = await createShareData({}, patientDetails, shareContext);
            const isMobileShare = /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
                navigator.userAgent,
            );

            if (isMobileShare && navigator.share) {
                await navigator.share({ title, text: message });
            } else {
                const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
                window.open(whatsappUrl, '_blank');
            }

            mixpanel.track('HandoutShared', {
                email: userDataStore?.userData?.emailAddress,
                shareMethod: isMobileShare ? 'Native Share' : 'WhatsApp',
                handoutTitle: shareTargetLabel,
            });

            closeDialog();
        } catch (error) {
            console.error('Quick share WhatsApp error:', error);
            openAuthAwareErrorDialog(error);
        } finally {
            setIsBlocking(false);
        }
    };

    const ensureCopyLink = async () => {
        if (generatedLink) {
            return generatedLink;
        }
        const patientDetails = buildPatientDetails();
        const shareContext = getShareContext();
        const link = await generateLinkOnly({}, patientDetails, shareContext);
        setGeneratedLink(link);
        return link;
    };

    const handleCopyLink = async () => {
        if (isAuthExpiredDialogVisible()) {
            return;
        }

        setIsBlocking(true);
        try {
            const link = await ensureCopyLink();
            await navigator.clipboard.writeText(link);
            mixpanel.track('HandoutShared', {
                email: userDataStore?.userData?.emailAddress,
                shareMethod: 'Copy handout',
                handoutTitle: shareTargetLabel,
            });
            showSnackbar?.('Link copied to clipboard.', 'success');
            closeDialog();
        } catch (error) {
            console.warn('Clipboard API failed, using fallback method.', error);
            try {
                const link = await ensureCopyLink();
                const textArea = document.createElement('textarea');
                textArea.value = link;
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                const successful = document.execCommand('copy');
                if (!successful) {
                    throw new Error('Fallback copy command failed');
                }
                mixpanel.track('HandoutShared', {
                    email: userDataStore?.userData?.emailAddress,
                    shareMethod: 'Copy handout',
                    handoutTitle: shareTargetLabel,
                });
                showSnackbar?.('Link copied to clipboard.', 'success');
                closeDialog();
                document.body.removeChild(textArea);
            } catch (fallbackError) {
                console.error('Fallback copy failed:', fallbackError);
                openAuthAwareErrorDialog(fallbackError);
            }
        } finally {
            setIsBlocking(false);
        }
    };

    useEffect(() => {
        if (step !== 'share' || shareMethod !== 'copy' || generatedLink) {
            return;
        }

        let isMounted = true;
        const loadLink = async () => {
            try {
                setIsBlocking(true);
                const link = await ensureCopyLink();
                if (isMounted) {
                    setGeneratedLink(link);
                }
            } catch (error) {
                console.error('Failed to generate link:', error);
                openAuthAwareErrorDialog(error);
            } finally {
                if (isMounted) {
                    setIsBlocking(false);
                }
            }
        };

        loadLink();

        return () => {
            isMounted = false;
        };
    }, [step, shareMethod, generatedLink]);

    const dialogHeight = useMemo(() => {
        const shareKey = shareMethod === 'whatsapp' ? 'whatsapp' : shareMethod === 'copy' ? 'copy' : 'email';
        return dialogHeights.share[shareKey];
    }, [shareMethod]);
    const shouldUseAutoDialogHeight = useCompactDialog || step === 'patient';

    if (!handout) {
        return null;
    }

    return (
        <>
            <Dialog
                open={open}
                onClose={closeDialog}
                PaperProps={{
                    sx: {
                        borderRadius: '30px !important',
                        padding: useCompactDialog ? '30px 20px' : '30px 28px',
                        width: useCompactDialog ? 'calc(100% - 32px)' : '711px',
                        maxWidth: useCompactDialog ? 'calc(100% - 32px)' : '711px',
                        height: shouldUseAutoDialogHeight ? 'auto' : `${dialogHeight}px`,
                        maxHeight: 'calc(100dvh - 32px)',
                        boxSizing: 'border-box',
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        overflowX: 'hidden',
                        overflowY: 'auto',
                    },
                }}
                maxWidth={false}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: { xs: '8px', sm: '24px' },
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: 'Inter, sans-serif',
                                fontWeight: 600,
                                fontSize: { xs: 26, sm: 32 },
                                lineHeight: { xs: '26px', sm: '32px' },
                                letterSpacing: { xs: '-1.04px', sm: '-1.28px' },
                                color: designTokens.colors.textPrimary,
                                pr: '44px',
                            }}
                        >
                            {step === 'patient'
                                ? 'Add patient details'
                                : isBundledHandouts
                                  ? 'Share handouts'
                                  : 'Share handout'}
                        </Typography>
                        <IconButton
                            onClick={closeDialog}
                            sx={{
                                p: 0,
                                width: { xs: '28px', sm: '36px' },
                                height: { xs: '28px', sm: '36px' },
                                background: 'rgb(246, 246, 246)',
                                borderRadius: { xs: '11.928px', sm: '50%' },
                                flexShrink: 0,
                            }}
                        >
                            <CloseIcon sx={{ width: { xs: '16px', sm: '24px' }, height: { xs: '16px', sm: '24px' } }} />
                        </IconButton>
                    </Box>

                    <Typography
                        sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: { xs: 14, sm: 18 },
                            lineHeight: { xs: '18px', sm: '18px' },
                            letterSpacing: { xs: '-0.56px', sm: '-0.72px' },
                            color: designTokens.colors.textSecondary,
                            mb: step === 'patient' ? { xs: '16px', sm: '30px' } : { xs: '12px', sm: '20px' },
                        }}
                    >
                        {step === 'patient'
                            ? 'Add patient details to share handout'
                            : `How would you like to share ${isBundledHandouts ? 'these handouts' : 'this handout'} with your client?`}
                    </Typography>

                    {step === 'patient' && (
                        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                            <RadioGroup
                                value={inputMethod}
                                onChange={(event) => setInputMethod(event.target.value)}
                                sx={{ mb: { xs: '18px', sm: '28px' }, gap: { xs: 0.75, sm: 1 } }}
                            >
                                <FormControlLabel
                                    value="manual"
                                    control={<Radio sx={{ color: '#FF669B', '&.Mui-checked': { color: '#FF669B' } }} />}
                                    label="Enter patient details"
                                    sx={radioLabelSx}
                                />
                                <FormControlLabel
                                    value="select"
                                    control={<Radio sx={{ color: '#FF669B', '&.Mui-checked': { color: '#FF669B' } }} />}
                                    label="Select existing patient"
                                    disabled={isLoadingPatients || patients.length === 0}
                                    sx={radioLabelSx}
                                />
                                {showProvetOption && (
                                    <FormControlLabel
                                        value="provet"
                                        control={
                                            <Radio sx={{ color: '#FF669B', '&.Mui-checked': { color: '#FF669B' } }} />
                                        }
                                        label="Select Provet patient"
                                        disabled={!provetPatients.length}
                                        sx={radioLabelSx}
                                    />
                                )}
                            </RadioGroup>

                            {inputMethod === 'manual' && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontFamily: 'Inter, sans-serif',
                                                fontSize: { xs: 14, sm: 18 },
                                                lineHeight: { xs: '14px', sm: '18px' },
                                                letterSpacing: { xs: '-0.56px', sm: '-0.72px' },
                                                color: designTokens.colors.textSecondary,
                                                mb: { xs: '8px', sm: '12px' },
                                            }}
                                        >
                                            {PATIENT_NAME_LABEL}
                                        </Typography>
                                        <TextField
                                            value={manualPatientName}
                                            onChange={(e) => setManualPatientName(e.target.value)}
                                            fullWidth
                                            sx={inputFieldSx}
                                            InputProps={{ sx: inputFieldPropsSx }}
                                        />
                                    </Box>
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontFamily: 'Inter, sans-serif',
                                                fontSize: { xs: 14, sm: 18 },
                                                lineHeight: { xs: '14px', sm: '18px' },
                                                letterSpacing: { xs: '-0.56px', sm: '-0.72px' },
                                                color: designTokens.colors.textSecondary,
                                                mb: { xs: '8px', sm: '12px' },
                                            }}
                                        >
                                            {CLIENT_NAME_LABEL}
                                        </Typography>
                                        <TextField
                                            value={manualClientSurname}
                                            onChange={(e) => setManualClientSurname(e.target.value)}
                                            fullWidth
                                            sx={inputFieldSx}
                                            InputProps={{ sx: inputFieldPropsSx }}
                                        />
                                    </Box>
                                </Box>
                            )}

                            {inputMethod === 'select' && (
                                <Box sx={{ mb: { xs: 2, sm: 0 } }}>
                                    <Typography
                                        sx={{
                                            fontSize: { xs: 14, sm: 18 },
                                            lineHeight: { xs: '14px', sm: '18px' },
                                            letterSpacing: { xs: '-0.56px', sm: '-0.72px' },
                                            color: designTokens.colors.textSecondary,
                                            mb: { xs: '8px', sm: '12px' },
                                        }}
                                    >
                                        Select patient
                                    </Typography>
                                    <Autocomplete
                                        options={patients}
                                        getOptionLabel={formatPatientAutocompleteLabel}
                                        value={selectedPatient}
                                        onChange={(event, newValue) => setSelectedPatient(newValue)}
                                        noOptionsText={patientAutocompleteNoOptionsText}
                                        renderOption={renderPatientAutocompleteOption}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                sx={inputFieldSx}
                                                InputProps={{ ...params.InputProps, sx: inputFieldPropsSx }}
                                            />
                                        )}
                                        fullWidth
                                        disabled={isLoadingPatients || patients.length === 0}
                                        sx={patientAutocompleteSx}
                                    />
                                </Box>
                            )}

                            {inputMethod === 'provet' && showProvetOption && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography
                                        sx={{
                                            fontSize: 18,
                                            lineHeight: '18px',
                                            letterSpacing: '-0.72px',
                                            color: designTokens.colors.textSecondary,
                                            mb: '12px',
                                        }}
                                    >
                                        Select a patient
                                    </Typography>
                                    <Autocomplete
                                        options={provetPatients}
                                        getOptionLabel={(option) =>
                                            `${option?.name || ''}${option?.id ? ` (#${option.id})` : ''}`.trim()
                                        }
                                        isOptionEqualToValue={(option, value) => option?.url === value?.url}
                                        value={selectedProvetPatient}
                                        onChange={(event, newValue) => setSelectedProvetPatient(newValue)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                sx={inputFieldSx}
                                                InputProps={{ ...params.InputProps, sx: inputFieldPropsSx }}
                                            />
                                        )}
                                        fullWidth
                                        disabled={isSingleProvetPatient}
                                    />
                                </Box>
                            )}

                            {!isLoadingPatients && patients.length === 0 && !showProvetOption && (
                                <Typography sx={{ fontSize: 12, color: designTokens.colors.textSecondary, mb: 2 }}>
                                    No patients found. Use manual entry to add patient details.
                                </Typography>
                            )}

                            {!provetPatients.length && inputMethod === 'provet' && (
                                <Typography sx={{ fontSize: 12, color: designTokens.colors.textSecondary, mb: 2 }}>
                                    No Provet patients found.
                                </Typography>
                            )}

                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: '24px', sm: '28px' } }}>
                                <Button sx={continueButtonSx} onClick={handleContinue} disabled={isContinueDisabled()}>
                                    Continue
                                </Button>
                            </Box>
                        </Box>
                    )}

                    {step === 'share' && (
                        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-start',
                                    gap: { xs: '8px', sm: 2 },
                                    mb: { xs: '12px', sm: 2.5 },
                                    flexWrap: { xs: 'nowrap', sm: 'wrap' },
                                }}
                            >
                                {shareMethods.map((method) => (
                                    <Button
                                        key={method.value}
                                        sx={shareTabButtonSx(shareMethod === method.value)}
                                        onClick={() => setShareMethod(method.value)}
                                    >
                                        <img
                                            src={method.icon}
                                            alt={method.label}
                                            style={{
                                                ...iconStyle,
                                                width: isMobile ? 20 : 22,
                                                height: isMobile ? 20 : 22,
                                            }}
                                        />
                                        {!isMobile && method.label}
                                    </Button>
                                ))}
                            </Box>

                            {shareMethod === 'email' && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                                    <Box>
                                        <Typography
                                            sx={{
                                                fontFamily: 'Inter, sans-serif',
                                                fontSize: { xs: 14, sm: 18 },
                                                lineHeight: { xs: '14px', sm: '18px' },
                                                letterSpacing: { xs: '-0.56px', sm: '-0.72px' },
                                                color: designTokens.colors.textSecondary,
                                                mb: { xs: '8px', sm: '12px' },
                                            }}
                                        >
                                            Client's email
                                        </Typography>
                                        <TextField
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            fullWidth
                                            sx={inputFieldSx}
                                            InputProps={{ sx: inputFieldPropsSx }}
                                        />
                                    </Box>
                                    {/* TODO: Box below is commented out, do not remove it */}
                                    {/* <Box sx={{ mt: { xs: '14px', sm:'33px' } }}>
                                    <Typography
                                        sx={{
                                            fontSize: { xs: 14, sm:24 },
                                            lineHeight: { xs: '14px', sm:'24px' },
                                            letterSpacing: { xs: '-0.56px', sm:'-0.96px' },
                                            color: designTokens.colors.textSecondary,
                                            mb: { xs: '8px', sm:'20px' },
                                        }}
                                    >
                                        Message (optional)
                                    </Typography>
                                    <TextField
                                        value={emailMessage}
                                        onChange={(e) => setEmailMessage(e.target.value)}
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: { xs: '8px', sm:'20px' },
                                                minHeight: 70,
                                                backgroundColor: '#FFFFFF',
                                                '& fieldset': { borderColor: '#E5E5E5', borderWidth: { xs: '0.6px', sm:'1.5px' } },
                                                '&:hover fieldset': { borderColor: '#E5E5E5' },
                                                '&.Mui-focused fieldset': { borderColor: '#FF669B' },
                                            },
                                        }}
                                        InputProps={{
                                            sx: {
                                                ...inputFieldPropsSx,
                                                height: 'auto',
                                                alignItems: 'flex-start',
                                            },
                                        }}
                                    />
                                </Box> */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            mt: { xs: '24px', sm: 'auto' },
                                            pt: { sm: '24px' },
                                        }}
                                    >
                                        <Button sx={actionButtonSx} onClick={handleSendEmail}>
                                            {isMobile ? 'Send email' : 'Send'}
                                        </Button>
                                    </Box>
                                </Box>
                            )}

                            {shareMethod === 'whatsapp' && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                                    <Typography
                                        sx={{
                                            fontFamily: 'Inter, sans-serif',
                                            fontSize: { xs: 14, sm: 18 },
                                            lineHeight: { xs: '18px', sm: '18px' },
                                            letterSpacing: { xs: '-0.56px', sm: '-0.72px' },
                                            color: designTokens.colors.textSecondary,
                                        }}
                                    >
                                        {'Use '}
                                        <Box component="span" sx={{ fontWeight: 500 }}>
                                            WhatsApp
                                        </Box>
                                        {' to send the handout below'}
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            mt: { xs: '24px', sm: 'auto' },
                                        }}
                                    >
                                        <Button sx={whatsappButtonSx} onClick={handleShareWhatsapp}>
                                            Send via WhatsApp
                                        </Button>
                                    </Box>
                                </Box>
                            )}

                            {shareMethod === 'copy' && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                                    <Typography
                                        sx={{
                                            fontFamily: 'Inter, sans-serif',
                                            fontSize: { xs: 14, sm: 18 },
                                            lineHeight: { xs: '18px', sm: '18px' },
                                            letterSpacing: { xs: '-0.56px', sm: '-0.72px' },
                                            color: designTokens.colors.textSecondary,
                                            mb: { xs: '12px', sm: '20px' },
                                        }}
                                    >
                                        Copy the link below to share the handout via your chosen communication channel.
                                    </Typography>
                                    <TextField
                                        value={generatedLink}
                                        placeholder="Generating link..."
                                        fullWidth
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                height: { xs: '34px', sm: 52 },
                                                borderRadius: { xs: '8px', sm: '14px' },
                                                backgroundColor: '#FFFFFF',
                                                paddingRight: '0 !important',
                                                '& fieldset': {
                                                    borderColor: '#E5E5E5',
                                                    borderWidth: { xs: '0.6px', sm: '1.5px' },
                                                },
                                                '&:hover fieldset': { borderColor: '#E5E5E5' },
                                                '&.Mui-focused fieldset': { borderColor: '#FF669B' },
                                            },
                                        }}
                                        InputProps={{
                                            readOnly: true,
                                            endAdornment: (
                                                <InputAdornment
                                                    position="end"
                                                    sx={{
                                                        height: { xs: '34px', sm: '52px' },
                                                        maxHeight: 'none',
                                                        ml: 0,
                                                        mr: 0,
                                                    }}
                                                >
                                                    <IconButton
                                                        onClick={handleCopyLink}
                                                        disableRipple
                                                        sx={{
                                                            width: { xs: '40px', sm: '52px' },
                                                            height: { xs: '34px', sm: '52px' },
                                                            borderRadius: { xs: '0 8px 8px 0', sm: '0 14px 14px 0' },
                                                            backgroundColor: '#FF669B',
                                                            '&:hover': { backgroundColor: '#e65588' },
                                                        }}
                                                    >
                                                        <img
                                                            src={CopyIcon}
                                                            alt="Copy link"
                                                            style={{
                                                                width: isMobile ? 14 : 22,
                                                                height: isMobile ? 14 : 22,
                                                                filter: 'brightness(0) invert(1)',
                                                            }}
                                                        />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                            sx: {
                                                ...inputFieldPropsSx,
                                                height: { xs: '34px', sm: '52px' },
                                                minHeight: 'unset',
                                                paddingRight: '0 !important',
                                            },
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            mt: { xs: '24px', sm: 'auto' },
                                        }}
                                    >
                                        <Button sx={actionButtonSx} onClick={handleCopyLink}>
                                            Copy link
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            </Dialog>

            <BlockingSpinner open={open && (isBlocking || isLoadingPatients)} />
            <ErrorDialog
                open={errorDialogOpen}
                handleClose={() => setErrorDialogOpen(false)}
                title={errorDialogTitle}
                message={errorDialogMessage}
            />
        </>
    );
};

export default QuickShareDialog;
